// @flow
/* eslint-disable react/jsx-no-bind */
import * as React from 'react';
import { AutoSizer } from 'react-virtualized';
import { themr, type ThemeType } from 'react-css-themr';
import { compose } from 'recompose';
import { Column, Box, Text } from '@locus-taxy/neo-ui';
import { translate, Trans, type TFunction } from 'react-i18next';
import classnames from 'classnames';
import isArray from 'lodash/isArray';

import Icon from 'common/components/core/icon';

import { type PjpMetricCardProps } from 'common/entity/pjpMaster/pjpDataTypes/insights/PjpMetricCardDataTypes';

import defaultTheme from './PjpMetricChartCard.scss';

type RenderChartParams = {
    height: number,
    width: number,
    relativeWidth: number,
    relativeHeight: number
};

type OwnProps = {
    metricCard: PjpMetricCardProps,
    hideAggregateInHeader: boolean,
    dialogActive: boolean,
    gridWidth: number,
    minCardHeight: number,
    cardHeight: number,
    currentCols: number,
    renderChart: (params: RenderChartParams) => React.Node,
    className?: ?string,
    theme?: ThemeType,
    renderFooter: () => ?React.Element<any>,
    cardHeader: () => React.Element<any>,
    emptyState: boolean
};

type Props = {
    ...OwnProps,
    t: TFunction,
    theme: ThemeType
};

class PjpMetricChartCard extends React.PureComponent<Props> {
    isDataEmpty() {
        const { chart } = this.props.metricCard;
        if (chart && chart.type) {
            const { data } = chart;
            const { currentPlanData, existingPlanData } = data;
            if (chart.type === 'BAR') {
                if (Array.isArray(currentPlanData?.dataKeys)) {
                    if (currentPlanData?.dataKeys.length === 0) {
                        return true;
                    }
                    if (
                        currentPlanData?.dataKeys.every(
                            data => !Array.isArray(data) && data.value === 0
                        )
                    ) {
                        return true;
                    }
                } else if (
                    currentPlanData?.values &&
                    currentPlanData?.values.length === 0
                ) {
                    return true;
                }
                return false;
            }

            if (!currentPlanData && !existingPlanData) {
                return true;
            }

            if (currentPlanData && !existingPlanData) {
                if (!isArray(currentPlanData) || !currentPlanData?.length) {
                    return true;
                } else {
                    const [value] = currentPlanData;
                    return value ? false : true;
                }
            }
            if (existingPlanData && !currentPlanData) {
                if (!isArray(existingPlanData) || !existingPlanData?.length) {
                    return true;
                } else {
                    const [value] = existingPlanData;
                    return value ? false : true;
                }
            }
            return false;
        }
        return true;
    }

    _getRelativeDimensions = ({ width }) => {
        const { props } = this;
        return {
            relativeWidth:
                props.currentCols / (props.gridWidth / (width + 20 + 20)),
            relativeHeight: props.cardHeight / props.minCardHeight
        };
    };

    shouldHideOnResize() {
        const { chart } = this.props.metricCard;
        return (
            chart &&
            chart.data &&
            (chart.type === 'LINE' || chart.type === 'BAR') &&
            (chart.data.currentPlanData?.values.length *
                chart.data.currentPlanData?.dataKeys.length >
                50 ||
                chart.data.existingPlanData?.values.length *
                    chart.data.existingPlanData?.dataKeys.length >
                    50)
        );
    }

    renderEmptyState() {
        return (
            <Box height="fluid">
                <Column>
                    <Text>
                        Oops, it seems data for the metric is unavailable.
                        Please try some other configuration.
                    </Text>
                </Column>
            </Box>
        );
    }

    renderEmptyChartState() {
        const { theme } = this.props;
        return (
            <div className={theme.emptyStateWrap}>
                <div className={theme.emptyState}>
                    <Trans i18nKey="analytics:noDataToVisualize">
                        <span>
                            <span className={theme.semibold}> No data</span> to
                            visualize
                        </span>
                    </Trans>
                </div>
            </div>
        );
    }

    renderChartResizingState() {
        const { theme, t } = this.props;
        return (
            <div className={theme.resizingStateWrap}>
                <div className={theme.resizingState}>
                    <Icon
                        theme={theme}
                        className={theme.resizeIcon}
                        icon="resize"
                    />
                    {t('resizingGraph')}
                </div>
            </div>
        );
    }

    renderCard = () => {
        const { props } = this;
        const hideOnResize = this.shouldHideOnResize();
        const { renderFooter, emptyState } = props;

        return (
            <div
                className={classnames(props.theme.metricCard, props.className, {
                    [props.theme.hideAggregateInHeader]:
                        props.hideAggregateInHeader,
                    [props.theme.hideOnResize]: hideOnResize
                })}
            >
                {props.metricCard.status.hasData ? (
                    <>
                        {props.cardHeader()}
                        {hideOnResize ? this.renderChartResizingState() : null}
                        {emptyState || this.isDataEmpty() ? (
                            this.renderEmptyChartState()
                        ) : (
                            <div
                                className={
                                    props.renderFooter || props.cardHeader
                                        ? props.theme.rechartsContainer
                                        : props.theme.rechartsPjpContainer
                                }
                            >
                                <AutoSizer
                                    children={(size: {
                                        height: number,
                                        width: number
                                    }) => {
                                        const {
                                            relativeWidth,
                                            relativeHeight
                                        } = this._getRelativeDimensions({
                                            width: size.width
                                        });

                                        return this.props.renderChart({
                                            width: size.width,
                                            height: size.height,
                                            relativeWidth,
                                            relativeHeight
                                        });
                                    }}
                                />
                            </div>
                        )}

                        {renderFooter()}
                    </>
                ) : (
                    this.renderEmptyState()
                )}
            </div>
        );
    };

    render() {
        return this.renderCard();
    }
}

export default (compose(
    themr('PjpMetricChartCard', defaultTheme),
    translate('translations')
)(PjpMetricChartCard): React.AbstractComponent<OwnProps>);
